import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import LanguageDropdown from "./LanguageDropdown";
import CommunityConnect from "./CommunityConnect";
import { getData } from "../data/data";
import "./CommunityPageLayout.css";

const LABEL_MAP = {
  "/african": "African",
  "/american": "American",
  "/armenian": "Armenian",
  "/chinese": "Chinese",
  "/eritrean": "Eritrean",
  "/ethiopian": "Ethiopian",
  "/european": "European",
  "/indian": "Indian",
  "/japanese": "Japanese",
  "/korean": "Korean",
  "/lebanese": "Lebanese",
  "/mexican": "Mexican",
  "/middleeastern": "Middle Eastern",
  "/nigerian": "Nigerian",
  "/palestinian": "Palestinian",
  "/persian": "Persian",
  "/syrian": "Syrian",
  "/thai": "Thai",
  "/turkish": "Turkish",

  "/visitresturants": "Restaurants",
  "/visitgroceries": "Grocery Stores",
  "/visitfoodtrucks": "Food Trucks",
  "/visitveganplaces": "Vegan",
  "/visitkosherplaces": "Kosher",
  "/farmersmarket": "Farmers Market",
  "/fleamarketplace": "Flea Market",
  "/cleaning-services": "Cleaning Services",
  "/itservices": "IT Services",
  "/realtor": "Realtor",
  "/photography": "Photography",
  "/gardening": "Gardening",
  "/catering": "Catering",
  "/murals": "Murals",
  "/wreaths": "Wreaths",
  "/clothing": "Clothing & Footwear",
  "/toys": "Toys",
  "/jewelry": "Jewelry",
  "/childcare": "Child Care",
  "/electrical": "Electrical",
  "/plumbing": "Plumbing",
  "/elderlycare": "Elderly Care",
};

const I18N_TO_LEGACY_LANGUAGE = {
  en: "english",
  am: "amharic",
  ar: "arabic",
  bn: "bengali",
  zh: "chinese",
  fil: "filipino",
  fr: "french",
  gu: "gujarati",
  de: "german",
  he: "hebrew",
  hi: "hindi",
  ig: "igbo",
  it: "italian",
  ja: "japanese",
  ko: "korean",
  om: "oromo",
  pt: "portuguese",
  es: "spanish",
  sw: "swahili",
  ti: "tigrinya",
  wo: "wolof",
};

function legacyLanguageFromI18n(i18n) {
  const code = String(
    i18n?.resolvedLanguage || i18n?.language || "en"
  )
    .split("-")[0]
    .toLowerCase();

  return I18N_TO_LEGACY_LANGUAGE[code] || "english";
}

function humanizePath(pathname) {
  const cleaned = String(pathname || "")
    .toLowerCase()
    .split("?")[0]
    .split("#")[0];

  if (LABEL_MAP[cleaned]) return LABEL_MAP[cleaned];

  const raw = cleaned.replace(/^\/+/, "");
  if (!raw) return "Community";

  return raw
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function CommunityPageLayout() {
  const location = useLocation();
  const { i18n } = useTranslation();

  /*
   * IMPORTANT:
   * Do NOT keep a separate community-sidebar language in localStorage.
   *
   * The Hub language dropdown already changes global i18n.
   * When the user opens a community page inside the SPA, this layout
   * reads that same global language, so the sidebar automatically
   * carries over the Hub selection.
   *
   * Because i18n.js initializes as English on a fresh browser reload,
   * refreshing still returns the site to English as requested.
   */
  const [language, setLanguage] = useState(() =>
    legacyLanguageFromI18n(i18n)
  );

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("goh:community-rail-collapsed") === "1";
    } catch {
      return false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 980 : false
  );

  /*
   * If the global language changes while this layout is mounted,
   * keep the sidebar dropdown and translated community list in sync.
   */
  useEffect(() => {
    const syncFromGlobalLanguage = () => {
      setLanguage(legacyLanguageFromI18n(i18n));
    };

    syncFromGlobalLanguage();
    i18n.on("languageChanged", syncFromGlobalLanguage);

    return () => {
      i18n.off("languageChanged", syncFromGlobalLanguage);
    };
  }, [i18n]);

  useEffect(() => {
    const onResize = () => {
      const nextMobile = window.innerWidth <= 980;
      setIsMobile(nextMobile);
      if (!nextMobile) setMobileOpen(false);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);

    try {
      localStorage.setItem(
        "goh:community-rail-collapsed",
        next ? "1" : "0"
      );
    } catch {
      // ignore
    }
  };

  const { frequentlyVisitedCommunities, communities } = getData(language);
  const currentLabel = humanizePath(location.pathname);

  return (
    <div className="community-layout-page">
      <Header />

      <div
        className={[
          "community-layout-shell",
          collapsed ? "is-collapsed" : "",
          mobileOpen ? "is-mobile-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isMobile && mobileOpen && (
          <button
            type="button"
            className="community-layout__backdrop"
            aria-label="Close community menu"
            onClick={() => setMobileOpen(false)}
          />
        )}

        <aside
          className="community-layout-rail"
          aria-label="Community navigation"
        >
          <div className="community-layout-card">
            <div className="community-layout-rail-top">
              <span className="community-layout-eyebrow">
                Community pages
              </span>

              <h2>Browse communities faster.</h2>

              <p>
                Move between community pages without jumping back to the main
                Hub. On mobile, this becomes a slide-in drawer.
              </p>

              <div className="community-layout-rail-actions">
                {!isMobile ? (
                  <button
                    type="button"
                    className="community-layout-toggle"
                    onClick={toggleCollapsed}
                    aria-label={
                      collapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                  >
                    <span className="toggle-text">
                      {collapsed ? "Expand" : "Collapse"}
                    </span>
                    <span className="toggle-icon" aria-hidden="true">
                      {collapsed ? "»" : "«"}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="community-layout-toggle"
                    onClick={() => setMobileOpen(false)}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>

            <div className="community-layout-language">
              <LanguageDropdown
                selectedLanguage={language}
                onLanguageChange={setLanguage}
              />
            </div>

            <div className="community-layout-divider" />

            <CommunityConnect
              countries={communities}
              frequentlyVisited={frequentlyVisitedCommunities}
            />
          </div>
        </aside>

        <main className="community-layout-content">
          <nav
            className="community-layout-breadcrumbs"
            aria-label="Breadcrumb"
          >
            <Link
              to="/hub"
              className="community-layout-breadcrumbs__back"
            >
              ← Back to Hub
            </Link>

            <span className="community-layout-breadcrumbs__sep">
              •
            </span>

            <span className="community-layout-breadcrumbs__current">
              {currentLabel}
            </span>
          </nav>

          {isMobile && (
            <div className="community-layout-mobilebar">
              <button
                type="button"
                className="community-layout-mobiletoggle"
                onClick={() => setMobileOpen((prev) => !prev)}
              >
                {mobileOpen
                  ? "Close community menu"
                  : "Browse communities"}
              </button>
            </div>
          )}

          <Outlet />
        </main>
      </div>

      <Footer />
    </div>
  );
}
